/**
 * Finds the open spots of a zod schema, so a route contract can refuse them when it is DEFINED.
 *
 * A response schema is closed when every value in it is named: no `z.any()`, no `z.unknown()`, no object that lets
 * extra keys through (`.passthrough()`, `z.looseObject`, `.catchall(x)`). A request body must also be STRICT at every
 * level: the repository's rule is that an object schema that does not reject unknown keys strips them silently, so a
 * form field nobody declared vanishes without an error.
 *
 * It reads the schema through `schema._zod.def` (zod 4), with runtime checks and no hand-written shape of the
 * definition, and it FAILS CLOSED: a schema type it does not recognise is reported as `unsupported`, never accepted.
 * The only way to accept a free document is `jsonDocument` (see `json-document.ts`).
 *
 * Two more questions, for the places that need them: in a RESPONSE (`jsonOnly`) every value must be able to travel as
 * JSON, and in a REQUEST (`documents: 'bounded'`) a free document must declare its `maxBytes`. `visitNode` lets a
 * caller (`defineScreen`, looking for regions) use the same walk instead of writing another.
 *
 * `transform`: a response describes the JSON on the wire, so a `.transform()` after a schema is reported (`transform`):
 * map the value before returning it. A request accepts a typed schema followed by `.transform()` (the transform runs
 * after the input is validated and is not walked); `z.preprocess` is reported, because its input has no type. A bare
 * `z.transform()` outside a pipe stays `unsupported` (it fails closed).
 *
 * In a RESPONSE (`jsonOnly`) these are `not-json`: a literal of `undefined`, a bigint or a symbol; an `optional`
 * element of a list or tuple (it travels as `null`); and an `undefined` or `void` member of a union (its key is
 * dropped). A request accepts them.
 */

import type { z } from 'zod/v4'

import { isJsonDocument, jsonDocumentMaxBytes } from './json-document.js'

/** `closed`: nothing may be `any`/`unknown`/open. `strict`: also every object must reject unknown keys. */
export type SchemaClosure = 'closed' | 'strict'

export type OpenSchemaReason =
  | 'any'
  | 'unknown'
  | 'passthrough'
  | 'catchall'
  | 'not-strict'
  | 'not-json'
  | 'transform'
  | 'unbounded-document'
  | 'unsupported'

export interface FindOpenSchemasOptions {
  /** The first segment of every path: `data`, `body`, `response`, `query`. */
  root: string
  closure: SchemaClosure
  /**
   * The schema describes what goes over the wire as JSON (a RESPONSE): a `Date`, `bigint`, `Map`, `Set`, `undefined`,
   * `void`, `symbol`, `NaN` or function cannot, so it is reported as `not-json` instead of read as a leaf.
   */
  jsonOnly?: boolean
  /** `bounded` (a REQUEST): a `jsonDocument` with no `maxBytes` is reported as `unbounded-document`. */
  documents?: 'allow' | 'bounded'
  /**
   * Called every time the walk reaches a schema, with its path, before anything is decided about it. A recursive
   * reference calls it again (before the cut), and a `pipe` calls it for its input and its output with the same path.
   */
  visitNode?: (node: z.ZodType, path: string) => void
}

export interface OpenSchemaFinding {
  /** Where it is, from `options.root`: `data.withdrawals[].metadata`, `body.filters`, `response.page.items[]`. */
  readonly path: string
  readonly reason: OpenSchemaReason
  /** What to do about it, when the reason alone does not say (`describeOpenSchemaFindings` prints it). */
  readonly hint?: string
}

/** `path (reason)` or `path (reason: hint)`, joined by commas: the text of the errors that name the findings. */
export function describeOpenSchemaFindings(findings: readonly OpenSchemaFinding[]): string {
  return findings
    .map((finding) =>
      finding.hint
        ? `${finding.path} (${finding.reason}: ${finding.hint})`
        : `${finding.path} (${finding.reason})`,
    )
    .join(', ')
}

/** Types with no children to walk and nothing open about them. */
const LEAF_TYPES: ReadonlySet<string> = new Set([
  'string',
  'number',
  'boolean',
  'null',
  'literal',
  'enum',
  'date',
  'bigint',
  'nan',
  'undefined',
  'void',
  'never',
  'template_literal',
])

/**
 * What cannot travel as JSON. In a response they are `not-json`. In a request `date`, `bigint`, `nan`, `undefined` and
 * `void` are leaves (a coerced date comes in as text); the rest stay `unsupported`.
 */
const NON_JSON_TYPES: ReadonlySet<string> = new Set([
  'date',
  'bigint',
  'nan',
  'undefined',
  'void',
  'map',
  'set',
  'symbol',
  'function',
  'promise',
  'file',
])

/** Wrappers whose `innerType` is the schema that matters. */
const WRAPPER_TYPES: ReadonlySet<string> = new Set([
  'optional',
  'nullable',
  'default',
  'prefault',
  'readonly',
  'nonoptional',
  'catch',
])

/** The definition of a schema as plain data: every field is checked at runtime before it is used. */
function readDefinition(node: z.ZodType): Record<string, unknown> {
  return node._zod.def as unknown as Record<string, unknown>
}

/** An object schema that declares at least one field. */
function hasNamedFields(node: z.ZodType): boolean {
  const def = readDefinition(node)
  if (def.type !== 'object') return false
  const shape = def.shape
  return typeof shape === 'object' && shape !== null && Object.keys(shape).length > 0
}

/** True when a `literal` carries a value JSON cannot hold (`z.literal(undefined)`, `z.literal(1n)`). */
function hasNonJsonLiteral(def: Record<string, unknown>): boolean {
  const values = def.values
  if (!Array.isArray(values)) return false
  return values.some((value) => ['undefined', 'bigint', 'symbol'].includes(typeof value))
}

function isSchema(value: unknown): value is z.ZodType {
  return typeof value === 'object' && value !== null && '_zod' in value
}

export function findOpenSchemas(
  schema: z.ZodType,
  options: FindOpenSchemasOptions,
): OpenSchemaFinding[] {
  const findings: OpenSchemaFinding[] = []
  // The schemas being walked on the way down from the root. A recursive schema (a `z.lazy`, or a getter such as
  // `get children() { return z.array(Node) }`) reaches one of them again: it is not walked a second time. Its
  // findings were reported where it was first met. It is a stack, not a visited set, so a schema that is used in two
  // places is still walked, and reported, in both.
  //
  // A cycle that goes through NO object with named fields is a free document of any depth (`z.json()` is a lazy
  // union of scalars, arrays and records of itself): it is open, and `jsonDocument` is the way to say so. A typed
  // recursive schema (a tree of named nodes) always passes through an object with fields. `named` counts the objects
  // with fields on the way down; each ancestor remembers how many there were when it was entered.
  const ancestors = new Map<z.ZodType, { named: number; selfNamed: boolean }>()
  let named = 0

  const report = (path: string, reason: OpenSchemaReason, hint?: string): void => {
    findings.push(hint === undefined ? { path, reason } : { path, reason, hint })
  }

  /** The `type` of a schema slot, or `undefined` when it holds no schema. */
  const typeOf = (child: unknown): unknown =>
    isSchema(child) ? readDefinition(child).type : undefined

  /** Walks the element of a list or tuple; an `optional` one is an `undefined` that travels as `null`. */
  const visitElement = (child: unknown, path: string): void => {
    if (options.jsonOnly === true && typeOf(child) === 'optional') {
      report(path, 'not-json', 'an undefined inside a list travels as null: use .nullable()')
      return
    }
    visitChild(child, path)
  }

  const visitChild = (child: unknown, path: string): void => {
    if (isSchema(child)) {
      visit(child, path)
    } else {
      // A slot that should hold a schema and does not: say so, do not skip it.
      report(path, 'unsupported')
    }
  }

  const walkChildren = (node: z.ZodType, type: string, path: string): void => {
    const def = readDefinition(node)

    if (WRAPPER_TYPES.has(type)) {
      visitChild(def.innerType, path)
      return
    }

    switch (type) {
      case 'object': {
        const catchall = def.catchall
        if (isSchema(catchall)) {
          const catchallType = catchall._zod.def.type
          if (catchallType === 'unknown' || catchallType === 'any') report(path, 'passthrough')
          else if (catchallType !== 'never') report(path, 'catchall')
        } else if (options.closure === 'strict') {
          report(path, 'not-strict')
        }
        const shape = def.shape
        if (typeof shape === 'object' && shape !== null) {
          for (const [key, child] of Object.entries(shape)) visitChild(child, `${path}.${key}`)
        }
        return
      }
      case 'array':
        visitElement(def.element, `${path}[]`)
        return
      case 'record':
        visitChild(def.valueType, `${path}{}`)
        return
      case 'tuple': {
        const items = Array.isArray(def.items) ? def.items : []
        items.forEach((child: unknown, index: number) => visitElement(child, `${path}[${index}]`))
        if (def.rest !== null && def.rest !== undefined) visitElement(def.rest, `${path}[]`)
        return
      }
      case 'union': {
        const members = Array.isArray(def.options) ? def.options : []
        for (const child of members) {
          const memberType = typeOf(child)
          if (options.jsonOnly === true && (memberType === 'undefined' || memberType === 'void')) {
            report(path, 'not-json', 'use .optional() instead of a union with undefined')
            continue
          }
          visitChild(child, path)
        }
        return
      }
      case 'intersection':
        visitChild(def.left, path)
        visitChild(def.right, path)
        return
      case 'pipe':
        // The input matters for a request body (an object that is not strict strips keys BEFORE the pipe), the output
        // for a response; both are walked, except a `transform`, which has no schema to walk (see the header).
        if (typeOf(def.in) === 'transform') {
          report(
            path,
            'transform',
            'z.preprocess has no typed input: use z.coerce.<type><T>() or a typed schema followed by .transform()',
          )
          visitChild(def.out, path)
          return
        }
        visitChild(def.in, path)
        if (typeOf(def.out) === 'transform') {
          if (options.jsonOnly === true) {
            report(
              path,
              'transform',
              'a response describes the JSON on the wire: map the value before returning it',
            )
          }
          return
        }
        visitChild(def.out, path)
        return
      case 'lazy':
        if (typeof def.getter !== 'function') {
          report(path, 'unsupported')
          return
        }
        visitChild((def.getter as () => unknown)(), path)
        return
      default:
        // custom, map, set, promise, function, symbol, file, transform… and any type this walk does not know.
        report(path, 'unsupported')
    }
  }

  const visit = (node: z.ZodType, path: string): void => {
    options.visitNode?.(node, path)

    if (isJsonDocument(node)) {
      if (options.documents === 'bounded' && jsonDocumentMaxBytes(node) === undefined) {
        report(path, 'unbounded-document')
      }
      return
    }

    const def = readDefinition(node)
    const type = def.type

    if (typeof type !== 'string') {
      report(path, 'unsupported')
      return
    }
    if (type === 'any') {
      report(path, 'any')
      return
    }
    if (type === 'unknown') {
      report(path, 'unknown')
      return
    }
    if (options.jsonOnly === true && NON_JSON_TYPES.has(type)) {
      report(path, 'not-json')
      return
    }
    // A literal of `undefined`, a bigint or a symbol is a leaf by type and not JSON by value.
    if (options.jsonOnly === true && type === 'literal' && hasNonJsonLiteral(def)) {
      report(path, 'not-json')
      return
    }
    if (LEAF_TYPES.has(type)) return

    const seen = ancestors.get(node)
    if (seen) {
      if (!seen.selfNamed && named === seen.named) report(path, 'unknown')
      return
    }

    const selfNamed = hasNamedFields(node)
    if (selfNamed) named += 1
    ancestors.set(node, { named, selfNamed })
    try {
      walkChildren(node, type, path)
    } finally {
      ancestors.delete(node)
      if (selfNamed) named -= 1
    }
  }

  try {
    visit(schema, options.root)
  } catch (error) {
    // A factory that builds a NEW recursive schema on every call (`() => z.object({ c: z.lazy(factory) })`) has no
    // node to recognise: the walk cannot finish, and it says so instead of overflowing without a path.
    if (!(error instanceof RangeError)) throw error
    report(options.root, 'unsupported')
  }
  return findings
}
